import { ScheduleException } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Heading, ModalControlModalProps, NakedButton, Tray } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../../hooks/use-scheduling-location-info';
import { useAddEditRuleExceptionForm } from '../hooks/use-create-edit-rule-exception-form';
import { AddEditRuleExceptionsForm } from './add-edit-rule-exceptions-form';

dayjs.extend(customParseFormat);

type Props = {
  locationId: string;
  modalProps: ModalControlModalProps;
  selectedException: ScheduleException | null;
  onClose: () => void;
};

export const AddEditRuleExceptionsTray = ({ locationId, modalProps, selectedException, onClose }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { isMultiLocation, getScopeName } = useSchedulingLocationInfo();
  const showLocationChip = isMultiLocation && locationId;
  const isEditMode = Boolean(selectedException);

  const closeModal = () => {
    reset();
    onClose();
  };

  const {
    isComplete,
    isSubmitting,
    values,
    getFieldProps,
    handleSubmit,
    handleTargetChange,
    handleRRuleChange,
    rRule,
    reset,
  } = useAddEditRuleExceptionForm({
    selectedException,
    locationId,
    isEditMode,
    closeModal,
  });

  const hasSpecificResourcesSelected =
    (values.providers?.length ?? 0) > 0 || (values.operatories?.length ?? 0) > 0 || (values.services?.length ?? 0) > 0;
  const hasCombinationResourcesSelected = Boolean(values.provider || values.operatory || values.service);
  const hasRequiredTargetSelection =
    values.target === 'ALL_RESOURCES'
      ? true
      : values.target === 'SPECIFIC_RESOURCES'
      ? hasSpecificResourcesSelected
      : hasCombinationResourcesSelected;
  const hasRequiredTimeValues = values.isAllDay || (Boolean(values.startTime) && Boolean(values.endTime));
  const isPastOccurrence = selectedException?.status === 'PAST';

  return (
    <Tray {...modalProps} width='large' onClose={closeModal}>
      <Tray.Header
        Buttons={
          <NakedButton onClick={closeModal}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        <div css={{ display: 'flex', flexDirection: 'column' }}>
          <Heading level={2}>{isEditMode ? t('Edit Rule Exception') : t('Add Rule Exception')}</Heading>
          {showLocationChip && <Chip.Location maxWidth={'fit-content'}>{getScopeName(locationId)}</Chip.Location>}
        </div>
      </Tray.Header>
      <Tray.Body css={{ padding: theme.spacing(0.5, 0.5) }}>
        <AddEditRuleExceptionsForm
          locationId={locationId}
          getFieldProps={getFieldProps}
          values={values}
          handleTargetChange={handleTargetChange}
          rRule={rRule}
          handleRRuleChange={handleRRuleChange}
          isPastOccurrence={isPastOccurrence}
        />
      </Tray.Body>
      <Tray.Actions
        disabledPrimary={
          !isComplete || isSubmitting || !hasRequiredTargetSelection || !hasRequiredTimeValues || isPastOccurrence
        }
        primaryLabel={isSubmitting ? t('Saving...') : t('Save')}
        onPrimaryClick={handleSubmit}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={closeModal}
      />
    </Tray>
  );
};
